import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
  Tooltip,
  CircularProgress,
  useTheme,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
  Label,
} from 'recharts';
import axios from 'axios';
import { db_url } from '../Api/BaseUrl';
import {
  TrendingUp,
  Person,
  Email,
  CheckCircle,
  Star,
  ShowChart,
  CalendarToday,
  Phone,
  AttachMoney,
  Numbers,
} from '@mui/icons-material';

export const DashbordPerformanceForAllUsers = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const [userlogin, setUserLogin] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMonth, setActiveMonth] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) setUserLogin(user);
  }, []);

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const { data } = await axios.get(
          `${db_url}api/v1/users/user-Pereformance-stages?email=${userlogin?.data?.email}`,
        );
        setPerformanceData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching performance data:', err);
        setLoading(false);
      }
    };
    if (userlogin?.data?.email) fetchPerformance();
  }, [userlogin?.data?.email]);

  // تحويل البيانات للرسم البياني المموج
  const chartData = performanceData
    ? Object.entries(performanceData.monthlyTotalUnitPrice).map(([month, value]) => ({
        name: month,
        value: value,
        fill: mode === 'dark' ? 'url(#colorGradientDark)' : 'url(#colorGradientLight)',
      }))
    : [];

  // Get best month
  const bestMonth = performanceData?.monthlyTotalUnitPrice
    ? Object.entries(performanceData.monthlyTotalUnitPrice).reduce(
        (best, current) => {
          return current[1] > best[1] ? current : best;
        },
        ['', 0],
      )
    : null;

  // Custom shape for the chart (جبل)
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const isBestMonth = bestMonth && bestMonth[0] === payload.name;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={isBestMonth ? '#FF6B35' : '#102dff'}
          stroke={mode === 'dark' ? '#fff' : '#fff'}
          strokeWidth={2}
        />
        {isBestMonth && (
          <circle
            cx={cx}
            cy={cy}
            r={10}
            fill="transparent"
            stroke="#FF6B35"
            strokeWidth={2}
            strokeDasharray="3 3"
          />
        )}
      </g>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress sx={{ color: '#FF6B35' }} thickness={5} size={70} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* العنوان الرئيسي */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            color: mode === 'dark' ? '#fff' : '#333',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <ShowChart sx={{ color: '#FF6B35', fontSize: 36 }} />
          Performance Dashboard
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: mode === 'dark' ? '#aaa' : '#666',
            maxWidth: '600px',
          }}
        >
          Track your sales performance, achievements, and growth over time
        </Typography>
      </Box>

      {/* معلومات المستخدم */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          background:
            mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(255, 107, 53, 0.1)'}`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 107, 53, 0.1)', width: 48, height: 48 }}>
                      <Person sx={{ color: '#FF6B35' }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}
                      >
                        Sales Agent
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ color: mode === 'dark' ? '#fff' : '#333' }}
                      >
                        {performanceData?.salesUser?.userName}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 107, 53, 0.1)', width: 48, height: 48 }}>
                      <Email sx={{ color: '#FF6B35' }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}
                      >
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ color: mode === 'dark' ? '#ccc' : '#555' }}>
                        {performanceData?.salesUser?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(6, 214, 160, 0.1)', width: 48, height: 48 }}>
                      <CheckCircle sx={{ color: '#06D6A0' }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}
                      >
                        Total Deals Closed
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: '#06D6A0' }}>
                        {performanceData?.totalDoneDeals}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background:
                    mode === 'dark' ? 'rgba(255, 107, 53, 0.1)' : 'rgba(255, 107, 53, 0.05)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255, 107, 53, 0.2)'}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: mode === 'dark' ? '#ccc' : '#666', mb: 1 }}
                >
                  Current Performance Score
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h3" fontWeight={800} sx={{ color: '#FF6B35' }}>
                    {performanceData?.totalDoneDeals * 10}
                  </Typography>
                  <TrendingUp sx={{ color: '#06D6A0', fontSize: 32 }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(performanceData?.totalDoneDeals * 10, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mt: 2,
                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #FF6B35, #FF8E53)',
                      borderRadius: 4,
                    },
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* الرسم البياني المموج (الجبل) */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
          background:
            mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: mode === 'dark' ? '#fff' : '#333',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <TrendingUp sx={{ color: '#FF6B35' }} />
              Monthly Sales Performance
            </Typography>
            <Typography variant="body2" sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}>
              Revenue growth over the past months with trend analysis
            </Typography>
          </Box>

          <Box sx={{ height: 350, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                onMouseMove={(e) => {
                  if (e.activePayload) {
                    setActiveMonth(e.activePayload[0].payload);
                  }
                }}
                onMouseLeave={() => setActiveMonth(null)}
              >
                <defs>
                  <linearGradient id="colorGradientLight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorGradientDark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF8E53" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#FF8E53" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: mode === 'dark' ? '#ccc' : '#666' }}
                  axisLine={{ stroke: mode === 'dark' ? '#444' : '#ddd' }}
                />
                <YAxis
                  tick={{ fill: mode === 'dark' ? '#ccc' : '#666' }}
                  axisLine={{ stroke: mode === 'dark' ? '#444' : '#ddd' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  contentStyle={{
                    background: mode === 'dark' ? '#2d2d44' : '#fff',
                    border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  }}
                  formatter={(value) => [`${value.toLocaleString()} AED`, 'Revenue']}
                  labelStyle={{ color: mode === 'dark' ? '#fff' : '#333', fontWeight: 600 }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#FF6B35"
                  strokeWidth={4}
                  fill={mode === 'dark' ? 'url(#colorGradientDark)' : 'url(#colorGradientLight)'}
                  fillOpacity={1}
                  activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* شهر المعلومة النشطة */}
            {activeMonth && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  p: 2,
                  minWidth: 200,
                  background:
                    mode === 'dark' ? 'rgba(45, 45, 68, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${mode === 'dark' ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255, 107, 53, 0.2)'}`,
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: mode === 'dark' ? '#fff' : '#333' }}
                >
                  {activeMonth.name}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#FF6B35' }}>
                  {activeMonth.value.toLocaleString()} AED
                </Typography>
                <Typography variant="caption" sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}>
                  Total Revenue
                </Typography>
              </Paper>
            )}
          </Box>

          {/* أفضل شهر */}
          {bestMonth && (
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Star sx={{ color: '#FFD700', fontSize: 28 }} />
              <Box>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ color: mode === 'dark' ? '#fff' : '#333' }}
                >
                  🏆 Best Performing Month: <span style={{ color: '#FF6B35' }}>{bestMonth[0]}</span>
                </Typography>
                <Typography variant="body2" sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}>
                  Achieved {bestMonth[1].toLocaleString()} AED in sales
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* تفاصيل اللييدز الشهرية */}
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{
          color: mode === 'dark' ? '#fff' : '#333',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CalendarToday sx={{ color: '#FF6B35' }} />
        Monthly Lead Details
      </Typography>

      {performanceData &&
        Object.entries(performanceData.monthlyUnitPriceDetails).map(([month, leads]) => (
          <Card
            key={month}
            sx={{
              mb: 3,
              borderRadius: 3,
              background:
                mode === 'dark'
                  ? 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    color: mode === 'dark' ? '#fff' : '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Chip
                    label={month}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #FF6B35, #FF8E53)',
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: mode === 'dark' ? '#aaa' : '#666', ml: 'auto' }}
                  >
                    {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
                  </Typography>
                </Typography>
              </Box>

              <Divider
                sx={{
                  mb: 3,
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                }}
              />

              <Grid container spacing={3}>
                {leads.map((lead) => (
                  <Grid item xs={12} sm={6} md={4} key={lead.leadId}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        background:
                          mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(255, 107, 53, 0.02)',
                        border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 107, 53, 0.1)'}`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${mode === 'dark' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(255, 107, 53, 0.1)'}`,
                          borderColor: '#FF6B35',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{ bgcolor: 'rgba(255, 107, 53, 0.1)', width: 40, height: 40 }}
                          >
                            <Person sx={{ color: '#FF6B35', fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ color: mode === 'dark' ? '#fff' : '#333' }}
                            >
                              {lead.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: mode === 'dark' ? '#aaa' : '#666' }}
                            >
                              ID: {lead.leadId}
                            </Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone sx={{ fontSize: 16, color: '#FF6B35' }} />
                              <Typography
                                variant="body2"
                                sx={{ color: mode === 'dark' ? '#ccc' : '#555' }}
                              >
                                {lead.phone}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Numbers sx={{ fontSize: 16, color: '#FF6B35' }} />
                              <Typography
                                variant="body2"
                                sx={{ color: mode === 'dark' ? '#ccc' : '#555' }}
                              >
                                Unit: {lead.unitnumber}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachMoney sx={{ fontSize: 16, color: '#06D6A0' }} />
                              <Typography
                                variant="body2"
                                sx={{ color: '#06D6A0', fontWeight: 600 }}
                              >
                                {lead.unit_price.toLocaleString()} AED
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}
    </Container>
  );
};
